import React, { useContext, useEffect } from "react";
import { Typography } from "core/typography/Typography";
import { FormattedMessage } from "react-intl";
import Button from "core/buttons/button/Button";
import ClearFiltersButton from "core/buttons/clear-filters-button/ClearFiltersButton";
import "./SharedDashboard.scss";
import InfoContext from "pages/InfoContext";
import {
  DASHBOARD_SHARED_SECTIONS,
  EXITS_COMPANIES_BY,
  QUERY_STATUS,
  VAR_TYPES,
} from "constants/constants";
import { SHARED_DASHBOARD_FORM_LABELS } from "constants/forms";
import {
  renderInputBranch,
  renderInputJob,
  renderInputJobCategoryTag,
  renderInputManager,
} from "components/form/CommonInputs";
import { sharedDashboardInputs } from "utils/sharedDashboardHelper";
import { getJobsInCategories } from "utils/functions";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const SharedDashboardForm = () => {
  const {
    control,
    errors,
    handleSubmit,
    register,
    setValue,
    watch,
    branchList,
    getOptions,
    personsByProfileList,
    jobList,
    jobCategoryList,
    getCategoriesJobIds,
    lang,
    section,
    onSubmit,
    infosBySection,
    setDirectHiringMobility,
    setIsValidated,
    setFilterIC,
    setFilterFDM,
    setFilterED,
    setFilterOK,
    setFilterCL,
    setFilterSS,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { ic, fdm, ed, ok, cl, ko } = DASHBOARD_SHARED_SECTIONS || {};
  const hasActiveFilters = () => {
    const currentSection = infosBySection[section];
    if (!currentSection?.filters) return false;

    const { subsidiary, manager, job, jobCategory, mobility, start_at } =
      currentSection.filters;

    const hasSubsidiary = subsidiary?.length > 0;
    const hasManager = manager?.length > 0;
    const hasJob = job?.length > 0;
    const hasJobCategory = jobCategory?.length > 0;
    const hasMobility = mobility?.length > 0;
    const hasStartAt = start_at?.length > 0;

    return (
      hasSubsidiary ||
      hasManager ||
      hasJob ||
      hasJobCategory ||
      hasMobility ||
      hasStartAt
    );
  };

  const clearAllFilters = () => {
    const sectionName = infosBySection[section]?.sectionName;

    const commonFilters = [
      [`${sectionName}_subsidiaries`, []],
      [`${sectionName}_managers`, []],
    ];

    const domainFilters = [
      [`${sectionName}_domain_categories`, []],
      [`${sectionName}_domains`, []],
    ];

    const sectionFilters = {
      [ic]: domainFilters,
      [fdm]: domainFilters,
      [ed]: [...domainFilters, ["mobility", ""]],
      [ok]: [["ok_start_at", []]],
      [ko]: [],
    };

    [...commonFilters, ...(sectionFilters[section] || [])].forEach(
      ([field, value]) => setValue(field, value)
    );
  };

  // Same cascade as the entry forms: a job the newly picked categories no
  // longer offer must go, otherwise an incoherent category/job pair is sent to
  // the server. Lives here because sharedDashboardInputs is a render function
  // and cannot hold an effect.
  const currentFilters = infosBySection[section]?.filters;
  const selectedCategoryIds = (currentFilters?.jobCategory || []).map(
    (elm) => elm?.value
  );
  const selectedCategoryKey = selectedCategoryIds.join(",");
  useEffect(() => {
    const selected = currentFilters?.job || [];
    if (!selected.length) return;
    const domainsField = `${infosBySection[section]?.sectionName}_domains`;
    // Removing the last category leaves no job selectable at all (redmine
    // 12939), so the jobs go with it: kept there they would be sent with every
    // search while the locked field gave no way to remove them.
    if (!selectedCategoryIds.length) {
      setValue(domainsField, []);
      return;
    }
    // Only prune against a referential that has actually landed: it arrives
    // after the filters restored from storage, and an empty one would read as
    // "these categories offer nothing" and wipe a legitimate pair.
    if (!jobCategoryList?.length) return;
    const kept = getJobsInCategories(
      selected,
      getCategoriesJobIds(selectedCategoryIds)
    );
    if (kept.length !== selected.length) setValue(domainsField, kept);
  }, [selectedCategoryKey, section, jobCategoryList]);

  useEffect(() => {
    const subscription = watch((values) => {
      const {
        direct_hiring_managers,
        direct_hiring_domain_categories,
        direct_hiring_domains,
        direct_hiring_subsidiaries,
        direct_hiring_date,
        mobility,
        companies_exits_subsidiaries,
        companies_exits_managers,
        companies_exits_date,
        exits_companies_by,
        intercontracts_subsidiaries,
        intercontracts_managers,
        intercontracts_domain_categories,
        intercontracts_domains,
        missions_exits_activation_subsidiaries,
        missions_exits_activation_managers,
        missions_exits_activation_domain_categories,
        missions_exits_activation_domains,
        ok_subsidiaries,
        ok_managers,
        long_leave_subsidiaries,
        long_leave_managers,
      } = values;

      const subsidiariesByTab = [
        { key: "ic", value: intercontracts_subsidiaries },
        { key: "fdm", value: missions_exits_activation_subsidiaries },
        { key: "ed", value: direct_hiring_subsidiaries },
        { key: "ok", value: ok_subsidiaries },
        { key: "cl", value: long_leave_subsidiaries },
        { key: "ko", value: companies_exits_subsidiaries },
      ];

      subsidiariesByTab.forEach(({ key, value }) => {
        if (value && value.length > 0) {
          localStorage.setItem(
            `shared_dashboard_${key}_subsidiary`,
            JSON.stringify(value)
          );
        } else {
          localStorage.removeItem(`shared_dashboard_${key}_subsidiary`);
        }
      });

      if (section === ic) {
        setFilterIC({
          intercontracts_subsidiaries: intercontracts_subsidiaries,
          intercontracts_managers: intercontracts_managers,
          intercontracts_domain_categories: intercontracts_domain_categories,
          intercontracts_domains: intercontracts_domains,
        });
      }

      if (section === fdm) {
        setFilterFDM({
          missions_exits_activation_subsidiaries:
            missions_exits_activation_subsidiaries,
          missions_exits_activation_managers:
            missions_exits_activation_managers,
          missions_exits_activation_domain_categories:
            missions_exits_activation_domain_categories,
          missions_exits_activation_domains: missions_exits_activation_domains,
        });
      }

      if (section === ed) {
        setFilterED({
          direct_hiring_subsidiaries: direct_hiring_subsidiaries,
          manager_mission_ed: direct_hiring_managers,
          dav_function_category_ed: direct_hiring_domain_categories,
          dav_function_ed: direct_hiring_domains,
          date: direct_hiring_date,
          mobility: mobility,
        });
      }

      if (section === ok) {
        setFilterOK({
          ok_subsidiaries: ok_subsidiaries,
          ok_managers: ok_managers,
        });
      }

      if (section === cl) {
        setFilterCL({
          long_leave_subsidiaries: long_leave_subsidiaries,
          long_leave_managers: long_leave_managers,
        });
      }

      if (section === ko) {
        setFilterSS({
          companies_exits_subsidiaries: companies_exits_subsidiaries,
          manager_companies_exits: companies_exits_managers,
          companies_exits_date: companies_exits_date,
          exits_companies_by: exits_companies_by,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [section]);

  return (
    <div className="shared-dashboard-forms">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="filters" defaultMessage="filtres" />}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="section-1">
          {sharedDashboardInputs({
            lang,
            control,
            register,
            watch,
            errors,
            setValue,
            getOptions,
            branchList,
            personsByProfileList,
            jobList,
            jobCategoryList,
            getCategoriesJobIds,
            section,
            infos: infosBySection[section],
            renderInputBranch,
            SHARED_DASHBOARD_FORM_LABELS,
            VAR_TYPES,
            renderInputManager,
            renderInputJob,
            renderInputJobCategoryTag,
            setDirectHiringMobility,
            EXITS_COMPANIES_BY,
            namesByFilters,
            getNamesByFilters,
          })}
        </div>
        <div className="buttons-container">
          <Button
            color="btn-medium gray-700"
            type={"submit"}
            id="button-search"
            onClick={() => setIsValidated(true)}
            disabled={
              infosBySection[section].loadDataStatus === QUERY_STATUS.LOADING
            }
          >
            <FormattedMessage id="search-button" defaultMessage="Rechercher" />
          </Button>
          <ClearFiltersButton
            onClear={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </form>
    </div>
  );
};

export default SharedDashboardForm;
